import { motion } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { SectionHead, Reveal } from "@/components/Shared";
import WorldMap from "@/components/WorldMap";

const CONSULTANTS_IMG =
  "https://images.pexels.com/photos/7433853/pexels-photo-7433853.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const INBOUND = [
  "Entity incorporation and regulatory navigation in India",
  "B2G alignment with national missions and procurement",
  "Local partnership and ecosystem orchestration",
];
const OUTBOUND = [
  "Market-entry strategy for UK, UAE, Singapore and US",
  "Investor and institutional introductions abroad",
  "IP posture and compliance for cross-border scale",
];

export default function GlobalReach() {
  return (
    <section id="global" data-testid="global-section" className="relative overflow-hidden py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead
          overline="Global Footprint"
          title={<>Borderless by <em className="gold-text not-italic">design.</em></>}
          testId="global-heading"
        />

        <div className="grid gap-4 lg:grid-cols-5">
          <Reveal className="min-w-0 lg:col-span-3">
            <div
              data-testid="corridor-map-card"
              className="relative h-full overflow-hidden border border-[rgba(212,175,55,0.2)] bg-[#0A1222]/70 p-6 backdrop-blur-sm sm:p-8"
            >
              <div className="flex items-center justify-between">
                <p className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                  Live Corridor Map
                </p>
                <p className="flex items-center gap-2 font-mono-alt text-[9px] uppercase tracking-[0.24em] text-[#64748B]">
                  <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-[#D4AF37]" />
                  New Delhi HQ
                </p>
              </div>
              <div className="mt-6">
                <WorldMap />
              </div>
              <p className="mt-4 font-mono-alt text-[9px] uppercase tracking-[0.24em] text-[#64748B]">
                Advisory corridors across four markets, run from one desk
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-2">
            <div className="relative h-full min-h-[320px]">
              <div className="clip-frame h-full border border-[rgba(212,175,55,0.35)] p-2">
                <div className="clip-frame relative h-full overflow-hidden">
                  <motion.img
                    data-testid="consultants-image"
                    src={CONSULTANTS_IMG}
                    alt="BYOT consultants in a strategy session with a client"
                    className="h-full w-full object-cover"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#070E1B]/90 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <p className="font-mono-alt text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]">
                      The Strategy Desk, In Session
                    </p>
                    <p className="mt-2 font-serif text-xl leading-snug text-[#F7F5F0]">
                      Cross-border mandates, architected in New Delhi.
                    </p>
                  </div>
                </div>
              </div>
              <div className="pointer-events-none absolute -right-4 -top-4 h-16 w-16 border-r border-t border-[#D4AF37]/50" />
            </div>
          </Reveal>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <Reveal>
            <article
              data-testid="global-inbound-card"
              className="group h-full border border-[rgba(212,175,55,0.2)] bg-[#0F1A2E]/85 p-8 backdrop-blur-md transition-colors duration-500 hover:border-[#D4AF37]/55 sm:p-10"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center border border-[#D4AF37]/50 text-[#D4AF37]">
                  <ArrowDownRight size={20} />
                </span>
                <p className="font-mono-alt text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">Global → India</p>
              </div>
              <h3 className="mt-6 font-serif text-2xl text-[#F7F5F0] sm:text-3xl">
                Setting up in India, without the friction.
              </h3>
              <ul className="mt-6 flex flex-col gap-4">
                {INBOUND.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-[#94A3B8]">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-[#D4AF37]" />
                    {item}
                  </li>
                ))}
              </ul>
            </article>
          </Reveal>

          <Reveal delay={0.12}>
            <article
              data-testid="global-outbound-card"
              className="group h-full border border-[rgba(212,175,55,0.2)] bg-[#0F1A2E]/85 p-8 backdrop-blur-md transition-colors duration-500 hover:border-[#D4AF37]/55 sm:p-10"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center border border-[#D4AF37]/50 text-[#D4AF37]">
                  <ArrowUpRight size={20} />
                </span>
                <p className="font-mono-alt text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">India → Global</p>
              </div>
              <h3 className="mt-6 font-serif text-2xl text-[#F7F5F0] sm:text-3xl">
                Indian deep-tech, on the world stage.
              </h3>
              <ul className="mt-6 flex flex-col gap-4">
                {OUTBOUND.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-[#94A3B8]">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-[#D4AF37]" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-2">
                {["United Kingdom", "UAE", "Singapore", "United States"].map((c) => (
                  <span
                    key={c}
                    data-testid={`corridor-chip-${c.toLowerCase().replace(/ /g, "-")}`}
                    className="border border-[rgba(212,175,55,0.3)] px-4 py-1.5 font-mono-alt text-[10px] uppercase tracking-[0.2em] text-[#E5C158]"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </article>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
